import { inArray } from '@teamworksdev/loco-sync-client';
import { useMemo } from 'react';
import { useQuery } from '../../../../loco-sync';
import { RouterOutput } from '../../../../trpc';
import { trpc } from '../../../../trpc/react';
import { getUrlTeam, useViewer } from '../../../context/viewer';
import { useAthletesQuery } from './useAthletesQuery';
import { useTracksQuery } from './useTracksQuery';
import { partitionTracks } from '../sort-tracks';
import { collectPublishedBlockTrackIds } from '../timeline/utils';
import { Phase, TrackPreview } from '../timeline/types';

type TeamDetail = RouterOutput['roster']['teamDetail'];
type Athlete = RouterOutput['roster']['teamAthletes'][0];

export type PlanData = {
  // Active tracks only
  tracks: TrackPreview[];
  // Unfiltered — the Archive section renders `filteredArchivedTracks` instead.
  archivedTracks: TrackPreview[];
  trackIds: string[];
  filteredTracks: TrackPreview[];
  // The Archive section's rendered list, scoped to the selected athlete.
  filteredArchivedTracks: TrackPreview[];
  refetchTracks: () => void;
  athletes: Athlete[];
  phases: Phase[];
  refetchPhases: () => void;
  teamDetail: TeamDetail | undefined;
};

export function usePlanData(
  teamId: string,
  selectedAthleteId?: string,
): PlanData {
  const { tracks: rawTracks, refetch: refetchTracks } = useTracksQuery(teamId);

  // These are teamProcedures scoped solely by the URL-derived `tw-team-ids`
  // header, so their enablement must gate on the same URL value — see the
  // invariant in app/context/viewer.tsx. The Plan tree stays mounted for a
  // render after the URL drops its team UUID (e.g. navigating to
  // /permissions); without this gate they refetch headerless and 400.
  const hasUrlTeam = getUrlTeam(window.location.pathname) !== '';

  // Athlete roster — Zero under the tracks surface flag, tRPC otherwise. Both
  // reads are multi-team reads (the tRPC route header-scoped, the Zero path
  // scoped by the viewer's session), so the enablement gate must be the URL
  // team — same invariant as above.
  const athletesQuery = useAthletesQuery({ enabled: hasUrlTeam });

  // Phases View revoked means the server denies `phase.phases`; letting the
  // query fire would surface the route-permission error through the query
  // client's error boundary and crash the whole planner. Gate it off and
  // fall back to no phases, exactly like the URL gating above.
  const { hasPermissions } = useViewer();

  const canViewPhases = hasUrlTeam && hasPermissions('plan.phases', 'view');

  const phasesQuery = trpc.phase.phases.useQuery(undefined, {
    staleTime: 10_000,
    enabled: canViewPhases,
  });

  const teamDetailQuery = trpc.roster.teamDetail.useQuery(undefined, {
    staleTime: 10_000,
    enabled: hasUrlTeam,
  });

  const trackIds = useMemo(() => rawTracks.map((t) => t.id), [rawTracks]);

  // Pull all blocks (including canceled) from loco-sync to derive
  // `hasPublishedBlocks` per track. Canceled blocks can still be published, so
  // we don't filter by status here — that's what the original SC-2365 bug was.
  // Soft-deleted blocks are excluded: they keep `hasPublishedWorkouts: true`
  // after deletion (SC-5492), which permanently disabled track delete.
  const { data: locoSyncBlocks = [] } = useQuery('Block', {
    trackId: trackIds.length > 0 ? inArray(trackIds) : undefined,
    deletedAt: null,
  });

  const publishedBlockTrackIds = useMemo(
    () => collectPublishedBlockTrackIds(locoSyncBlocks),
    [locoSyncBlocks],
  );

  const allTracks: TrackPreview[] = useMemo(
    () =>
      rawTracks.map((t) => ({
        ...t,
        hasPublishedBlocks: publishedBlockTrackIds.has(t.id),
      })),
    [rawTracks, publishedBlockTrackIds],
  );

  // Split into active vs archived. Active tracks keep their existing sort
  // order; archived tracks are re-sorted alphabetically. The timeline renders
  // active tracks only — archived tracks are surfaced via `archivedTracks`.
  const { active: activeTracks, archived: archivedTracks } = useMemo(
    () => partitionTracks(allTracks),
    [allTracks],
  );

  // Filter tracks by selected athlete if applicable
  const filteredTracks = useMemo(() => {
    if (selectedAthleteId) {
      return activeTracks.filter((track) =>
        isAthleteOnTrack(track, selectedAthleteId),
      );
    }
    return activeTracks;
  }, [activeTracks, selectedAthleteId]);

  // The Archive section is scoped to the selected athlete the same way the
  // active list is. `archivedTracks` itself stays unfiltered: it is also what
  // answers "is this sub-track's parent archived?", and a parent the athlete
  // filter removed must still answer that truthfully — otherwise an archived
  // parent reads as active and the UI offers an Unarchive the server rejects.
  const filteredArchivedTracks = useMemo(() => {
    if (selectedAthleteId) {
      return archivedTracks.filter((track) =>
        isAthleteOnTrack(track, selectedAthleteId),
      );
    }
    return archivedTracks;
  }, [archivedTracks, selectedAthleteId]);

  return {
    tracks: activeTracks,
    archivedTracks,
    trackIds,
    filteredTracks,
    filteredArchivedTracks,
    refetchTracks,
    athletes: athletesQuery.data ?? [],
    // A disabled query still serves whatever is already in the cache, and the
    // query key is unchanged when Phases View is revoked mid-session — so
    // `phasesQuery.data` could hand back phases the viewer may no longer see.
    // Suppress it at the hook boundary so revoked permission means no data.
    phases: canViewPhases ? (phasesQuery.data ?? []) : [],
    refetchPhases: phasesQuery.refetch,
    teamDetail: teamDetailQuery.data,
  };
}

/**
 * Shared by the active and archived athlete filters so the two can't drift —
 * the Archive section is scoped to the selected athlete exactly as the active
 * list is.
 */
function isAthleteOnTrack(track: TrackPreview, athleteId: string): boolean {
  return track.athletes.some((athlete) => athlete.profileId === athleteId);
}
